import { Breakpoint, ThemeColor } from '@/enums'
import {
  themeBreakpointDown,
  themeBreakpointUp,
  toThemePixels,
  themeColor,
} from '@/utils'
import { Card } from '@surein/ui'
import styled from 'styled-components'
import { ifProp } from 'styled-tools'
import { ImageFromCdn, Typography } from '../../components/atoms'
import { Column, Flex, Row } from '../../components/styled'

export const Container = styled(Column)`
  flex: 1;
  min-width: 0;
  gap: 32px;
`

export const FlexWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding-bottom: 48px;
  min-width: 0;

  ${themeBreakpointUp(Breakpoint.sm)} {
    max-width: 1000px;
    margin: 0 auto;
    padding: 0 16px;
  }
`

export const FaqsWrapper = styled.div`
  margin-top: 32px;
  background-color: #141e33;
`

export const RecommendationTitle = styled(Column)`
  gap: ${toThemePixels(4)};
  align-items: center;
  text-align: center;
  padding: 0 16px;
  max-width: 800px;
  margin: 0 auto;

  > span:first-child {
    text-transform: capitalize;
  }
`

export const RecommendationPriceSection2 = styled(Row)`
  gap: ${toThemePixels(1)};
  align-items: center;
  margin-bottom: ${toThemePixels(2)};
`

export const RecommendationPriceSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${toThemePixels(1)};

  > span:first-child {
    text-decoration: line-through;
  }

  ${themeBreakpointDown(Breakpoint.sm)} {
    flex-direction: column;
  }
`

export const InsurancePeriodSwitchWrapper = styled.div`
  margin: 0 15px 8px;

  ${themeBreakpointUp(Breakpoint.tab)} {
    margin: 0 0 30px;
  }
`

export const ProductsContainer = styled.div<{ columns: number }>`
  display: grid;
  gap: 24px;
  grid-template-columns: 30% 35% 30%;
  align-items: center;

  > div {
    height: 100%;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

export const MobileProductCard = styled(Card)<{
  shouldShowMismatchWarning: boolean
}>`
  height: 100%;

  border: 2px solid
    ${(props) =>
      props.shouldShowMismatchWarning
        ? themeColor(ThemeColor.warning1)
        : 'none'};
`

export const DesktopProductCard = styled(Card)`
  height: 100%;
`

export const ProductWrapper = styled(Row)`
  height: 100%;
`

export const Product = styled(Column)`
  position: relative;
  width: 100%;
  gap: ${toThemePixels(8)};
  padding: ${toThemePixels(4)};
  height: 100%;
  justify-content: space-between;
  opacity: ${(props) => (props.isProductAvailable ? 1 : 0.5)};
  pointer-events: ${(props) => (props.isProductAvailable ? 'auto' : 'none')};

  img {
    border-radius: ${toThemePixels(2.5)};
    height: ${toThemePixels(12.5)};
    width: ${toThemePixels(12.5)};
    border: 1px solid ${(props) => props.theme.color.b30};
  }
`

export const DiscountPlaceholder = styled.div`
  &::after {
    content: '.';
    visibility: hidden;
    line-height: 140%;
  }
`

export const Highlight = styled(Row)`
  align-items: center;
  gap: ${toThemePixels(1)};
  > span {
    gap: ${toThemePixels(1)};
  }
  & svg {
    margin-right: 4px;
    flex-shrink: 0;
  }
`

export const ProductSection = styled(Column)`
  gap: ${toThemePixels(6)};
  align-items: center;
`

export const ProductCardsContainer = styled.div`
  grid-column: col-start 2 / span 10 !important;
  height: fit-content;
  margin: ${toThemePixels(6)} 0;
  width: 100%;

  ${themeBreakpointDown(Breakpoint.sm)} {
    grid-column: col-start / span 2 !important;
  }

  ${themeBreakpointDown(Breakpoint.tab)} {
    margin: 0;
  }

  > div > div {
    overflow-x: hidden;
  }

  > div > div:nth-child(2) {
    height: 107.5%;
  }
`

export const BestOfferBadge = styled(Row)`
  background-color: ${(props) => props.theme.color.glass100};
  color: ${(props) => props.theme.color.info1};
  border-radius: 16px;
  padding: ${toThemePixels(1)} ${toThemePixels(3)};
  text-align: center;
  font-weight: 500;
  gap: 4px;
  align-items: center;
  border: ${ifProp('isBestChoice', '1px solid #0D5DFF')};

  svg {
    flex-shrink: 0;
  }

  span {
    white-space: nowrap;
  }
`

export const MobileCardFooter = styled(Flex)`
  gap: ${toThemePixels(3)};
  flex-direction: column;

  ${themeBreakpointDown(Breakpoint.md)} {
    > span {
      text-align: center;
    }
  }
`

export const TestimonialGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${toThemePixels(6)};
  margin-top: ${toThemePixels(6)};
  grid-column: col-start 2 / span 10 !important;
  height: fit-content;

  ${themeBreakpointDown(Breakpoint.tab)} {
    grid-template-columns: 1fr;
  }

  ${themeBreakpointDown(Breakpoint.md)} {
    padding: 0 16px;
  }

  > div {
    overflow-x: hidden;
  }
`

export const FaqContainer = styled(Column)`
  gap: ${toThemePixels(6)};
  padding: 64px 16px;
  background-color: #141e33;
  align-items: center;

  button,
  div,
  span {
    text-align: start;
    color: white;
    border: none;
  }

  button > div {
    background-color: rgba(54, 60, 69, 0.4);
  }

  > div {
    width: 100%;
    max-width: 1000px;
  }

  svg {
    stroke: white;
  }

  ${themeBreakpointUp(Breakpoint.tab)} {
    padding-bottom: 264px;
  }
`

export const ProtectionItem = styled(Row)`
  gap: ${toThemePixels(2)};
  align-items: center;
  > svg {
    flex-shrink: 0;
  }
`

export const CompaniesTrustImagesContainer = styled(Row)`
  gap: ${toThemePixels(6)};
  justify-content: center;
  align-items: center;

  > img {
    flex: 1;
    height: 27px;
  }

  > img:last-child {
    height: auto;
  }

  ${themeBreakpointDown(Breakpoint.sm)} {
    flex-wrap: wrap;
    > img {
      max-width: 70px;
    }
  }
`

export const WhatIsProtectedSection = styled(Column)`
  gap: 16px;

  ${themeBreakpointDown(Breakpoint.md)} {
    padding: 0 16px;
  }
`

export const TopActionsContainer = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  gap: ${toThemePixels(4)};
  padding: 0 ${toThemePixels(2)};

  span,
  svg,
  button {
    align-items: center;
    font-weight: 500;
    color: ${(props) => props.theme.color.b50};
    stroke: ${(props) => props.theme.color.b50};
  }

  button:hover:not(:disabled) {
    color: ${(props) => props.theme.color.b50};
    svg {
      stroke: ${(props) => props.theme.color.b50};
    }
  }
`

export const ComparisonTableCard = styled.div<{ variant: string }>`
  scroll-margin-top: 60px;
  background-color: ${(props) => props.theme.color.b0};
  border-radius: 16px;
  padding: 16px;

  table {
    table-layout: auto;
  }

  table svg {
    flex-shrink: 0;
  }
`

export const HeaderCancellationNotice = styled(Row)`
  padding: ${toThemePixels(2)} 0;
  width: 100%;
  background-color: #141e33;
  justify-content: center;
  align-items: center;
  gap: 8px;
`

export const HeaderArea = styled.div`
  position: sticky;
  top: 0;
  z-index: 10000;
  background-color: ${(props) => props.theme.color.b0};
  width: 100%;
`

export const RiskInsuredTypography = styled(Typography)`
  text-align: center;
  font-weight: 500;
`

export const RiskInsuredSpacer = styled(Row)`
  justify-content: center;
  margin-top: 24px;
`

export const CompaniesTrustContainer = styled(Column)`
  align-items: center;
  gap: ${toThemePixels(4)};

  span {
    font-weight: 500;
  }
`

export const ProductFooter = styled(Row)`
  justify-content: space-between;
  align-items: center;
  gap: ${toThemePixels(2)};
`

export const StarRating = styled(Row)`
  gap: ${toThemePixels(1)};
  align-items: center;
  cursor: pointer;

  > div > svg {
    transition: transform 0.2s ease-in-out;
  }

  &:hover {
    > div > svg {
      transform: rotate(180deg);
      transition: transform 0.2s ease-in-out;
    }
  }
`

export const IndependentBrokerContainer = styled(Column)`
  align-items: center;
  padding-bottom: 64px;
`

export const IndependentBrokerTextContainer = styled(Column)`
  gap: ${toThemePixels(6)};
  align-items: center;

  span {
    text-align: center;
  }

  ${themeBreakpointDown(Breakpoint.md)} {
    padding: 0 16px;
  }
`

export const RatingTooltipFootnote = styled(Typography)`
  font-weight: 500;
`

export const AbsoluteImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 387px;
  overflow: hidden;
`

export const StyledImageFromCdn = styled(ImageFromCdn)`
  height: 100%;
  width: 100%;
  position: absolute;
  object-fit: cover;
`

export const CollapsibleAdvisorContentWrapper = styled(Row)`
  gap: ${toThemePixels(2)};

  > svg {
    flex-shrink: 0;
  }
`

export const CountdownRow = styled(Row)`
  gap: 4px;
  align-items: center;
  cursor: ${ifProp('withTooltip', 'pointer', 'default')};
`

export const TestimonialCardWrapper = styled.div`
  > div {
    background: ${(props) => props.theme.color.b0};
  }
`
